/**
 * Google Tag Manager helpers. Converted from mixins/gtm.js.
 *
 * `setupGtm` now takes the GTM id explicitly instead of reaching for
 * `this.organizer` — composables have no access to the consuming component's
 * data, so the caller passes it in.
 */
export function useGtm() {
  function setupGtm(gtmId?: string) {
    if (!gtmId) return;

    if (!document.getElementById('gtm-script')) {
      // Load GTM without an inline <script> so a strict Content Security
      // Policy (script-src without 'unsafe-inline') is respected. This
      // replicates Google's bootstrap snippet: seed the dataLayer, fire the
      // gtm.start event, then load gtm.js from the allow-listed origin.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });

      const script = document.createElement('script');
      script.id = 'gtm-script';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
        gtmId
      )}`;
      document.head.appendChild(script);
    }

    if (!document.getElementById('gtm-noscript')) {
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.cssText = 'display:none;visibility:hidden';
      const noscript = document.createElement('noscript');
      noscript.id = 'gtm-noscript';
      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
    }
  }

  /**
   * Pushes a GA4 ecommerce event to the dataLayer, clearing the previous
   * ecommerce object first as recommended by Google.
   * See https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
   */
  function gtmPush(event: string, ecommerce: Record<string, any>) {
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object
    window.dataLayer.push({ event, ecommerce });
  }

  return { setupGtm, gtmPush };
}
